import { useEffect, useMemo, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  FileText, FileDown, Loader2, Share2, MessageCircle, Mail, Sparkles,
} from "lucide-react";
import BiSelect from "@/components/BiSelect";
import RulesetStamp from "@/components/tax/RulesetStamp";
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";

/**
 * FySummaryCard — Module 6 "One-page year-end summary" (FY totals + GST +
 * jobs + evidence coverage). Downloads or shares the server-generated
 * PDF from `/api/export/fy-summary-pdf`.
 *
 * The PDF is pre-fetched for the CURRENT FY on mount so iOS Safari's
 * native share sheet appears immediately on tap (see
 * useShareToAccountant docs for the cache/gesture reason).
 */
export default function FySummaryCard() {
  const currentFy = useMemo(() => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  }, []);
  const [fy, setFy] = useState(currentFy);
  const [withEvidence, setWithEvidence] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);

  const { accountantName, accountantEmail, shareVia, prefetchPdf } = useShareToAccountant();

  const fyLabel = `FY${String(fy).slice(-2)}`;
  const baseUrl = `/export/fy-summary-pdf?fy=${fy}`;
  const shareUrl = `${baseUrl}${withEvidence ? "&with_evidence=1" : ""}`;
  const filename = `buildintel-fy-summary-${fyLabel}.pdf`;

  // Pre-fetch the PDFs so tap→share is synchronous on iOS.
  useEffect(() => {
    prefetchPdf(baseUrl, filename);
    prefetchPdf(`${baseUrl}&with_evidence=1`, filename);
  }, [prefetchPdf, baseUrl, filename]);

  // Small preview of the numbers (from /api/tax/summary if you have it — for
  // now we lightly fetch a summary via a cheap aggregation call).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/tax/fy-summary?fy=${fy}`);
        if (!cancelled) setSummary(data);
      } catch (err) {
        devLog.debug("fy-summary preview fetch failed (ok)", err);
      }
    })();
    return () => { cancelled = true; };
  }, [fy]);

  // First tap now ALWAYS opens the in-app preview sheet — no direct
  // download / navigator.share on tap. Share happens from inside the
  // preview. Kept `downloadFile` reachable via desktop fallback only.
  const openPreview = () => {
    const text =
      `Hi${accountantName ? ` ${accountantName}` : ""}, here's the BuildIntel AI year-end summary for ${fyLabel}. Attached is a one-page overview — category totals, GST, jobs and evidence coverage.`;
    const subject = `${fyLabel} Year-End Summary`;
    setPreviewCfg({
      pdfUrl: shareUrl,
      filename,
      title: `${fyLabel} year-end summary`,
      description: "Review the pages before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  };
  const download = openPreview;
  // Retained for JSX below — every WhatsApp/Email/Share tap opens preview.
  // eslint-disable-next-line no-unused-vars
  const share = (_kind) => openPreview();

  return (
    <div className="bi-surface p-4 space-y-3" data-testid="fy-summary-card">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 border border-[#FF9500]/25 flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-[#FF9500]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500] font-heading font-bold">
            Year-end summary
          </div>
          <div className="font-heading font-bold text-[15px] text-white leading-tight">
            One-page {fyLabel} pack
          </div>
          <div className="text-[11px] text-white/50 leading-snug mt-0.5">
            Category totals, GST, jobs, and evidence coverage — send it to your accountant before lodgement.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">FY</label>
        <BiSelect
          data-testid="fy-summary-fy-picker"
          className="bi-input h-9 py-0 text-sm flex-1"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
        >
          {[currentFy + 1, currentFy, currentFy - 1, currentFy - 2].map((y) => (
            <option key={y} value={y}>{`FY${String(y).slice(-2)}`}</option>
          ))}
        </BiSelect>
        <label
          className="inline-flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer select-none"
          data-testid="fy-summary-with-evidence-label"
        >
          <input
            type="checkbox"
            data-testid="fy-summary-with-evidence"
            checked={withEvidence}
            onChange={(e) => setWithEvidence(e.target.checked)}
            className="accent-[#FF9500] w-3.5 h-3.5"
          />
          Attach receipts
        </label>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-2" data-testid="fy-summary-kpis">
          <Kpi label="Business" value={`$${Math.round(summary.business_total || 0).toLocaleString("en-AU")}`} testid="kpi-business" />
          <Kpi label="GST 1B" value={`$${Math.round(summary.business_gst || 0).toLocaleString("en-AU")}`} testid="kpi-gst" />
          <Kpi label="Evidence" value={`${summary.evidence_pct || 0}%`} testid="kpi-evidence" />
        </div>
      )}

      <button
        type="button"
        data-testid="fy-summary-download-btn"
        onClick={download}
        disabled={downloading}
        className="w-full h-11 rounded-xl bi-accent-btn text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
        Download {fyLabel} summary (PDF)
      </button>

      <button
        type="button"
        data-testid="fy-summary-ask-assistant"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("buildintel:open-chat", {
            detail: {
              prompt:
                `Am I ready to send my ${fyLabel} accountant pack? Give me a pack coach — strengths first, then issues, then next steps.`,
            },
          }));
        }}
        className="w-full h-10 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
      >
        <Sparkles className="w-4 h-4 text-[#FF9500]" />
        Ask assistant if this pack is ready
      </button>

      <div className="pt-2 border-t border-white/5" data-testid="fy-summary-share-row">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-2">
          <Share2 className="w-3 h-3" /> Send to {accountantName ? accountantName : "accountant"}
          {accountantEmail && (
            <span className="text-white/30 normal-case tracking-normal truncate">· {accountantEmail}</span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="fy-summary-share-whatsapp"
            onClick={() => share("whatsapp")}
            disabled={shareBusy}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider disabled:opacity-60"
          >
            <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
          </button>
          <button
            type="button"
            data-testid="fy-summary-share-email"
            onClick={() => share("email")}
            disabled={shareBusy}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider disabled:opacity-60"
          >
            <Mail className="w-4 h-4 text-[#60A5FA]" /> Email
          </button>
          <button
            type="button"
            data-testid="fy-summary-share-native"
            onClick={() => share("native")}
            disabled={shareBusy}
            className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider disabled:opacity-60"
          >
            {shareBusy ? <Loader2 className="w-4 h-4 animate-spin text-[#FF9500]" /> : <Share2 className="w-4 h-4 text-[#FF9500]" />}
            Share PDF
          </button>
        </div>
      </div>

      <AtoDisclaimer context="tax_summary" compact testId="fy-summary-disclaimer" />
      <div className="text-center"><RulesetStamp /></div>

      <PdfPreviewSheet
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
        pdfUrl={previewCfg?.pdfUrl}
        filename={previewCfg?.filename}
        title={previewCfg?.title}
        description={previewCfg?.description}
        shareText={previewCfg?.shareText}
        shareSubject={previewCfg?.shareSubject}
        testid="fy-summary-preview"
      />
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-white/5 px-2 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 font-heading">{label}</div>
      <div className="text-sm font-heading font-bold text-white tabular-nums truncate">{value}</div>
    </div>
  );
}
